/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import * as React from 'react';

import type { MessageDescriptor, WrappedComponentProps } from 'react-intl-next';
import { injectIntl } from 'react-intl-next';
import { css, jsx } from '@atlaskit/css';
import { EmptyStar, FullStar } from '../star';

import messages from './messages';

export interface StarRatingProps {
    value: number;
    onChange: (rating: number) => void;
    commentSubmitting: boolean;
}

export class StarRating extends React.Component<StarRatingProps & WrappedComponentProps> {
    labels: MessageDescriptor[] = [
        messages.zeroStarRating,
        messages.oneStarRating,
        messages.twoStarRating,
        messages.threeStarRating,
        messages.fourStarRating,
        messages.fiveStarRating,
    ];

    createStar = (rating: number, index: number) => {
        const { intl } = this.props;
        const label = intl.formatMessage(this.labels[index]);
        const id = `${index}-star`;
        const checked = rating === index;

        const changeHandler = () => {
            const { onChange, commentSubmitting } = this.props;
            if (!commentSubmitting) {
                onChange(index);
            }
        };

        if (index === 0) {
            if (rating === 0) {
                return (
                    <React.Fragment>
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label key={`${index}-label`} htmlFor={id} />
                        <input
                            css={hiddenInputStyles}
                            key={`${index}-input`}
                            type="radio"
                            id={id}
                            name="star-rating"
                            checked={checked}
                            onChange={changeHandler}
                        />
                    </React.Fragment>
                );
            }
            return undefined;
        }

        return (
            <React.Fragment>
                <label
                    key={`${index}-label`}
                    htmlFor={id}
                    data-test-id={`${id}-rating`}
                    data-test-checked={checked ? `${id}-rating-checked` : undefined}
                >
                    {checked || rating >= index ? <FullStar label={label} /> : <EmptyStar label={label} />}
                </label>
                <input
                    css={hiddenInputStyles}
                    key={`${index}-input`}
                    type="radio"
                    id={id}
                    name="star-rating"
                    checked={checked}
                    onChange={changeHandler}
                />
            </React.Fragment>
        );
    };

    getRating = () => {
        let rating: number;
        const propRating = this.props.value;
        if (propRating < 0) {
            rating = 0;
        } else if (propRating > 5) {
            rating = 5;
        } else {
            rating = propRating;
        }
        return rating;
    };

    render() {
        const rating = this.getRating();

        return (
            <div>
                {this.createStar(rating, 0)}
                {this.createStar(rating, 1)}
                {this.createStar(rating, 2)}
                {this.createStar(rating, 3)}
                {this.createStar(rating, 4)}
                {this.createStar(rating, 5)}
            </div>
        );
    }
}

const hiddenInputStyles = css({
    display: 'none',
});

export default injectIntl(StarRating);
